import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { missionPath } from "@/routes"
import type { UpcomingDeadlineRow } from "@/types/brand-dashboard"

/**
 * OKS-38 — Single mission row in the Upcoming Deadlines panel.
 *
 * Renders as a real `<a>` (Inertia `<Link>`) so:
 *   - browser focus styles + tab order work natively (UX Spec §6 a11y)
 *   - Cmd-click / right-click "open in new tab" behave like normal anchors
 *
 * Hover lifts the row to `bg-muted/40`. `min-h-11` ensures ≥44px tap
 * target (UX Spec §5 mobile responsive).
 *
 * Locale-aware date formatting via `Intl.DateTimeFormat(i18n.language)`:
 *   - en: "Apr 30, 2026"
 *   - fr: "30 avr. 2026"
 *
 * The mission title is allowed to truncate at 1 line on narrow widths;
 * the full title carries through as the anchor `title=` for keyboard +
 * screen-reader users (UX Spec §3 long-title edge).
 */
export interface DashboardDeadlineRowProps {
  row: UpcomingDeadlineRow
}

export function DashboardDeadlineRow({ row }: DashboardDeadlineRowProps) {
  const { t, i18n } = useTranslation()

  const formattedDeadline = formatDeadline(row.deadline, i18n.language)
  const crewLabel = t("brand_home.deadlines.crew_count", {
    count: row.crew_count,
  })
  const ariaLabel = t("brand_home.deadlines.row_aria", {
    title: row.title,
    date: formattedDeadline,
    crew: crewLabel,
  })

  return (
    <Link
      href={missionPath(row.id)}
      title={row.title}
      aria-label={ariaLabel}
      data-testid="dashboard-deadline-row"
      data-mission-id={row.id}
      className={cn(
        "group flex min-h-11 items-center justify-between gap-4 rounded-md px-3 py-2",
        "hover:bg-muted/40 focus-visible:bg-muted/40 transition-colors",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground truncate text-sm font-medium">
          {row.title}
        </span>
        <span className="text-muted-foreground text-xs">
          {formattedDeadline}
        </span>
      </div>
      <Badge variant="secondary" className="shrink-0">
        {crewLabel}
      </Badge>
    </Link>
  )
}

function formatDeadline(iso: string, locale: string): string {
  // ISO date strings are timezone-less — `new Date(iso)` would coerce
  // through the browser's locale, which can shift the day at midnight
  // boundaries. Parse the YYYY-MM-DD parts manually + build a UTC date
  // so the formatter sees the date the server intended, not a TZ-shifted
  // sibling.
  const [yyyy, mm, dd] = iso.split("-").map((part) => Number.parseInt(part, 10))
  if (!yyyy || !mm || !dd) return iso

  const date = new Date(Date.UTC(yyyy, mm - 1, dd))

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date)
}
